import { CheckboxState, type CheckboxData } from './types';
import {
  determineState,
  findNodeByName,
  propagateToParent,
  setNewState,
  updateStateRecursively,
} from './utils';

interface UseCheckboxStateArgs {
  data: CheckboxData[];
  values: Record<string, boolean>;
  setValue: (name: string, value: boolean) => void;
}

export function useCheckboxState(args: UseCheckboxStateArgs) {
  const { data, values, setValue } = args;

  const getCheckboxState = (name: string) => {
    const node = findNodeByName(name, data);

    if (!node) {
      return {
        checked: false,
        indeterminate: false,
      };
    }

    const state = determineState(values, node.children ?? []);
    return {
      checked: state === CheckboxState.CHECKED,
      indeterminate: state === CheckboxState.INDETERMINATE,
    };
  };

  const updateCheckboxState = (name: string, newValue: boolean) => {
    const node = findNodeByName(name, data);
    const oldState = getCheckboxState(name);
    let newState = newValue ? CheckboxState.CHECKED : CheckboxState.UNCHECKED;

    // REMARK: to keep behavior indeterminate -> checked -> unchecked
    if (oldState.indeterminate) {
      newState = CheckboxState.CHECKED;
    }

    if (!node) {
      return;
    }

    if (node.children) {
      updateStateRecursively(values, setValue, node.children, newState);
    }

    setNewState(name, newState, setValue);

    propagateToParent(node.name, data, values, setValue);
  };

  const changeSelectAll = (newValue: boolean) => {
    for (const node of data) {
      updateCheckboxState(node.name, newValue);
    }
  };

  const selectAllState = determineState(values, data);

  return {
    getCheckboxState,
    updateCheckboxState,
    selectAll: {
      state: {
        checked: selectAllState === CheckboxState.CHECKED,
        indeterminate: selectAllState === CheckboxState.INDETERMINATE,
      },
      change: changeSelectAll,
    },
  };
}
